import React, {useState} from 'react';
import {Box, Button, CircularProgress, Divider, InputBase, Stack, Typography} from '@mui/material';

import '../styles/AuthPage.scss';
import {Link, useNavigate} from 'react-router-dom';
import Cookies from 'js-cookie';
import {Logout} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';

import {login} from '../actions/api';
import {useSnackbarStore} from '@/global/store/SnackbarStore';
import {useUserStore} from '@/global/store/UserStore';
import type {IRole} from '../dto/AuthDto';
import PageHeader from '../../global/components/PageHeader';
import {setTokens} from '@/global/actions/main.api';
import {getMessageFromError} from "@/global/helpers/getMessageFromError";

interface IAuth {
    login: string;
    password: string;
}

function AuthPage() {

    const navigate = useNavigate();
    const { t } = useTranslation();
    const userInfo = useUserStore((state) => state);
    const [authForm, setAuthForm] = useState<IAuth>({
        login: '',
        password: ''
    });
    const [authFormLoading, setAuthFormLoading] = useState<boolean>(false);
    const notificationMessage = useSnackbarStore((state) => state.showMessage);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const setIsObserver = useUserStore((state) => state.setIsObserver);
    const setIsAdmin = useUserStore((state) => state.setIsAdmin);
    const setIsSuperAdmin = useUserStore((state) => state.setIsSuperAdmin);
    const setIsStudent = useUserStore((state) => state.setIsStudent);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        const name = e.target.name;
        setAuthForm((prev) => {
            return {
                ...prev,
                [name]: value,
            };
        });
    };

    const authenticate = () => {
        setAuthFormLoading(true);

        if (!authForm.password) {
            notificationMessage({
                message: t('auth.auth-page.required-password'),
                severity: 'error'
            });
            return;
        }

        if (!authForm.login) {
            notificationMessage({
                message: t('auth.auth-page.required-login'),
                severity: 'error'
            });
            return;
        }

        if (authForm.password && authForm.login) {
            login(authForm)
                .then(async (res) => {
                    notificationMessage({
                        message: 'Успешный вход в систему',
                        severity: 'success'
                    });
                    const {accessToken, refreshToken} = res.data;

                    if (accessToken && refreshToken) {
                        setTokens({accessToken, refreshToken});
                        setUserInfo(res.data);
                        roleNavigate(res.data.roles);
                    } else {
                        notificationMessage({
                            message: 'Не удалось получить JwT-токены авторизации. Обратитесь в службу технической поддержки',
                            severity: 'error'
                        });
                        return;
                    }
                })
                .catch((e) => {
                    notificationMessage({
                        message: getMessageFromError(e),
                        severity: 'error'
                    });
                })
                .finally(() => {
                    setAuthFormLoading(false);
                });
        }
    };

    const roleNavigate = (roles: IRole[]) => {
        let isAdmin = false;
        let isStudent = false;
        let isObserver = false;

        for (let i = 0; i < roles.length; i++) {
            const role = roles[i];
            if (role.role === 'admin') {
                setIsAdmin(true);
                isAdmin = true;
            } else if (role.role === 'student') {
                setIsStudent(true);
                isStudent = true;
            } else if (role.role === 'superadmin') {
                setIsSuperAdmin(true);
            } else if (role.role === 'observer') {
                console.warn(`IS OBSERVER`);
                setIsObserver(true);
                isObserver = true;
            } else {
                setIsAdmin(false);
                isAdmin = false;
            }
        }

        if (!isAdmin) {
            navigate('/');
        } else if (!isStudent) {
            navigate('/');
        }

        if (isAdmin || isObserver) {
            navigate('/');
        }
    };

    const logOut = () => {
        userInfo.logOut();
        Cookies.set('accessToken', '');
        Cookies.set('refreshToken', '');
    };

    return (
        <Box
            className={'auth App'}
            id={'auth'}
            sx={{p: 5}}
            flexDirection={'column'}
            alignItems={'center'}
        >

            <PageHeader authBar={true}/>

            <Stack
                flexDirection={'column'}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Typography
                    sx={{mt: 3}}
                    variant={'h5'}
                >
                    <b>
                        {t('auth.auth-page.header')}
                    </b>
                </Typography>
                <Typography>
                    {t('auth.auth-page.subheader')}
                </Typography>
            </Stack>

            {userInfo.iin && (
                <Stack
                    flexDirection={'column'}
                    sx={{
                        width: '434px',
                        p: '32px',
                        background: '#FFFFFF',
                        borderRadius: '16px',
                        mt: '81px'
                    }}
                >
                    <Typography variant={'h5'}>
                        {t('auth.auth-page.authorized-text')}
                    </Typography>

                    <Divider sx={{mt: '24px', mb: '24px'}}/>

                    <Button
                        variant={'contained'}
                        color={'primary'}
                        onClick={logOut}
                        sx={{height: '56px'}}
                    >
                        <Logout sx={{mr: 1}}/>
                        {t('auth.auth-page.logout-btn')}
                    </Button>

                </Stack>
            )}

            {!userInfo.iin && (
                <Stack
                    flexDirection={'column'}
                    sx={{
                        width: '434px',
                        p: '32px',
                        background: '#FFFFFF',
                        borderRadius: '16px',
                        mt: '81px'
                    }}
                >
                    <Stack
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Typography variant={'h5'} >
                            {t('auth.auth-page.title')}
                        </Typography>
                    </Stack>

                    <Stack
                        flexDirection={'column'}
                        gap={'16px'}
                        sx={{mt: '24px', mb: '24px'}}
                    >
                        <InputBase
                            type={'text'}
                            placeholder={t('auth.auth-page.login')}
                            value={authForm.login}
                            name={'login'}
                            onChange={handleInputChange}
                        />

                        <InputBase
                            type={'password'}
                            placeholder={t('auth.auth-page.password')}
                            value={authForm.password}
                            name={'password'}
                            onChange={handleInputChange}
                        />
                    </Stack>

                    {!authFormLoading && (
                    <Button
                        variant={'contained'}
                        onClick={() => {authenticate();}}
                        disabled={authFormLoading}
                        sx={{height: '56px'}}
                    >
                        {t('auth.auth-page.login-btn')}
                    </Button>
)}

                    {authFormLoading && <CircularProgress sx={{alignSelf: 'center'}}/>}

                    <Divider sx={{mt: '24px', mb: '24px'}}/>

                    {!authFormLoading && (
                    <Link
                        to={'/register'}
                        style={{width: '100%'}}
                    >
                        <Button
                            variant={'outlined'}
                            sx={{height: '56px', width: '100%'}}
                            color={'info'}
                            disabled={authFormLoading}
                        >
                            {t('auth.auth-page.register-btn')}
                        </Button>
                    </Link>
)}

                    {authFormLoading && (
                        <Button
                            variant={'outlined'}
                            sx={{height: '56px', width: '100%'}}
                            color={'info'}
                            disabled={authFormLoading}
                        >
                            {t('auth.auth-page.register-btn')}
                        </Button>
                      )}

                    {/*{!authFormLoading && <a href={'/UserOffer.docx'} download={true} style={{marginTop: '24px', width: '100%'}}>*/}
                    {/*    <Button*/}
                    {/*        variant={'outlined'}*/}
                    {/*        sx={{height: '56px', width: '100%'}}*/}
                    {/*        color={'info'}*/}
                    {/*        disabled={authFormLoading}*/}
                    {/*    >*/}
                    {/*        Пользовательское соглашение*/}
                    {/*    </Button>*/}
                    {/*</a>}*/}
                </Stack>
            )}
        </Box>
    );
}

export default AuthPage;